// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import { TooltipSettings } from './TooltipSettings';
import { HintSettings } from './DocumentHints';
import {
  computerCheckMessage,
  speedBoostCards,
  speedBoostMemoryShort,
  speedBoostSummary,
} from './customer-health-report';
/**
 * Speed check — the screen a support representative talks a customer through.
 *
 * Two people have to be able to read this without help: a support representative
 * on the telephone who needs to know whether the machine got slower, and a sales
 * engineer who needs to know whether a graphics card would change anything. So
 * every number on it is accompanied by the sentence that says what it means, and
 * nothing on it is named after the thing that produced it.
 *
 * The rules this screen is written to:
 *
 * - **Pages per minute.** Never milliseconds or seconds per page.
 * - **Pages per document is stated beside every throughput.** Understanding a
 *   document costs per document rather than per page, so a set of short
 *   documents makes the same machine look several times slower. A throughput
 *   without the document length beside it invites a false comparison.
 * - **First-batch and warmed-up figures sit in separate columns and are never
 *   combined.** One describes how long starting up costs; the other is the
 *   machine's speed.
 * - **No measurement vocabulary.** Separation quality is three counts of things
 *   an operator has seen happen — split correctly, torn in two, stuck together.
 *   The conventional names for them have been tried on a reader and produced "I
 *   have no idea what this table is saying".
 * - **No component names.** What each part of the pipeline *does*, never what it
 *   is or who makes it.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AccentSettings } from './AccentSettings';
import { PerformanceScore } from './PerformanceScore';
import { SpeedAdvice, useJobSkillCounts } from './SpeedAdvice';
import {
  cardNames,
  freeGraphicsMemoryState,
  processorName,
  type AdviceActionId,
  type SpeedAdviceFacts,
} from './speed-advice';
import { EmailAsk, useEmailAsks } from './EmailAsk';
import { speedReportWhyCode, type SpeedReportFacts } from '../shared/email-asks';
import { measuredPerformanceScore, type PerformanceCalibration } from '../shared/performance-score';
import { TooltipButton } from './TooltipButton';
import { TooltipTitle } from './TooltipTitle';
import {
  RefreshCw,
  ChevronRight,
  HardDrive,
  Activity,
  AppWindow,
  CheckCircle2,
  CircleHelp,
  Cpu,
  Database,
  Download,
  Gauge,
  Gpu,
  LockKeyhole,
  LoaderCircle,
  Moon,
  MousePointer2,
  Minus,
  Palette,
  Plus,
  RotateCw,
  Square,
  Sun,
  TriangleAlert,
} from 'lucide-react';

import {
  blockerPhrasing,
  latestPerformanceMeasurement,
  formatPagesPerMinute,
  overallPagesPerMinute,
  speedCheckDepths,
  speedTestBlockedLine,
  speedTestPausableBlockers,
  type SpeedCheckAvailability,
  type SpeedCheckDepth,
  type SpeedCheckDetail,
  type SpeedCheckState,
  type SpeedTestSamplesRoute,
} from '../shared/benchmark-plan';
import { OperatorErrorText } from './TechnicalDetails';
import type { TelemetryUiErrorCode } from '../shared/telemetry-catalog';
import {
  SPEED_TEST_WHY_HEADING,
  speedTestWhyLines,
  type SpeedTestWhyLine,
} from '../shared/speed-test-why';
import { WorkstationHealthPanel } from './WorkstationHealth';
import { StorageLocationPanel } from './StorageLocation';
import { SoftwareUpdatesCard } from './SoftwareUpdates';
import { SupportAccessCard } from './SupportAccess';
import './onboarding.css';
import { LicenseSettings } from './LicenseSettings';
import { updateActivityLine } from '../shared/update-channels';
import type {
  AppearanceMode,
  FolderAnalysisProgress,
  GraphicsMemoryAtScoredTest,
  InterfaceZoomState,
  JobDefinition,
  JobSpeedHistory,
  JobSpeedMeasurement,
  StoredBenchmarkRun,
  UpdateStatus,
  WorkstationHealthReport,
} from '../shared/desktop-api';
import {
  focusActiveWorkspaceSection,
  WorkspaceSectionNav,
  type WorkspaceSectionItem,
} from './WorkspaceSections';
import { hostHas } from '../shared/host-capabilities';
import { hostSettingsSections } from './settings-host-sections';
import {
  ConnectionManagement,
  useConnectionManagement,
  type ConnectionIssueContext,
} from './ConnectionsPanel';
import './settings-workspace.css';
import './performance-history.css';

export type SettingsSection =
  | 'overview'
  | 'jobs'
  | 'cluster'
  | 'license'
  | 'performance'
  | 'storage'
  | 'connections'
  | 'appearance'
  | 'updates'
  | 'support';
type ComputerReadIssueKey = 'history' | 'details' | 'availability' | 'state' | 'updates' | 'health';
type ComputerReadIssues = Partial<Record<ComputerReadIssueKey, string>>;

const connectionIssueSummary: Record<ConnectionIssueContext, string> = {
  load: 'Connections could not be read',
  add: 'A new connection could not be saved',
  check: 'A connection check did not finish',
  replace: 'A connection key could not be replaced',
  remove: 'A connection could not be removed',
};

const desktop = () => window.capturePointDesktop;

const issueText = (reason: unknown, fallback: string) =>
  reason instanceof Error && reason.message.trim() ? reason.message : fallback;

const dateLabel = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * "Settings" — everything configurable on this machine rather than in a job.
 *
 * The screen was called Speed check while five of its six cards were about the
 * workstation and one was about speed, so the only word an operator navigates by
 * described the smallest thing on it. Updates, the update channel, the graphics
 * card verdict and the overnight schedule had no other home; the running version
 * lived on a 10px rail chip that vanished below 1100px. They are all here now,
 * under the name of the thing they are all about.
 *
 * The word on screen is always "computer". "Workstation" survives in types and
 * comments and must never reach a label.
 */
export function SettingsPage({
  standalone = false,
  section,
  onSectionChange,
  appearance,
  appearanceChanging,
  onAppearance,
  interfaceZoom,
  onInterfaceZoom,
  appVersion,
  checking,
  checkNote,
  cudaActive,
  activeJob,
  onCheckForUpdate,
  onOpenJobs,
  jobsContent,
  clusterContent,
  performanceCalibration,
  training = null,
  onTeach,
  onTrain,
  speedTestRequest = 0,
  onSpeedTestRequestHandled,
}: {
  standalone?: boolean;
  section: SettingsSection;
  onSectionChange: (section: SettingsSection) => void;
  appearance: AppearanceMode;
  appearanceChanging: boolean;
  onAppearance: (mode: AppearanceMode) => Promise<void>;
  interfaceZoom: InterfaceZoomState | null;
  onInterfaceZoom: (action: 'in' | 'out' | 'reset') => Promise<void>;
  appVersion: string;
  checking: boolean;
  checkNote: string | null;
  /** Same fact the rail dot answers, from the engine's own health line. */
  cudaActive: boolean;
  activeJob: { id: string; name: string; settingUp?: boolean } | null;
  onCheckForUpdate: () => void;
  onOpenJobs: () => void;
  jobsContent?: import('react').ReactNode;
  clusterContent?: import('react').ReactNode;
  /** Frozen measured anchors supplied by the owner. No default fabricated rating. */
  performanceCalibration?: PerformanceCalibration | null;
  /** Training on a schedule on this computer, as the native scheduler holds it; null until read. */
  training?: SpeedAdviceFacts['training'];
  /** Up to you's buttons: the job's documents on Teach, and its Learning › Train. */
  onTeach?: (jobId: string) => void;
  onTrain?: (jobId: string) => void;
  /** Raised by the speed test hint's Run speed test: start the test here, once per request. */
  speedTestRequest?: number;
  onSpeedTestRequestHandled?: () => void;
}) {
  const [history, setHistory] = useState<StoredBenchmarkRun[]>([]);
  // Read here as well as inside the cards below, because the three header facts
  // are what route three different visitors to three different sections, and a
  // fact that says nothing until you scroll to its card is not a header fact.
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [health, setHealth] = useState<WorkstationHealthReport | null>(null);
  const [details, setDetails] = useState<SpeedCheckDetail[]>([]);
  const [storedCalibration, setStoredCalibration] = useState<PerformanceCalibration | null>(null);
  const [graphicsMemory, setGraphicsMemory] = useState<GraphicsMemoryAtScoredTest | null>(null);
  const [state, setState] = useState<SpeedCheckState | null>(null);
  const [availability, setAvailability] = useState<SpeedCheckAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const openSection = useCallback(
    (next: SettingsSection) => {
      onSectionChange(next);
      window.requestAnimationFrame(() => focusActiveWorkspaceSection(pageRef.current));
    },
    [onSectionChange],
  );
  useEffect(() => {
    // A section this host does not have (a deep link, a restored session) lands on appearance.
    if (!hostSettingsSections([{ id: section, label: '' }]).length) openSection('appearance');
  }, [openSection, section]);
  const [appearanceFailure, setAppearanceFailure] = useState('');
  const connectionState = useConnectionManagement();
  const { connections, connectionsLoading, connectionIssue } = connectionState;
  const [readIssues, setReadIssues] = useState<ComputerReadIssues>({});
  const [startIssue, setStartIssue] = useState<string | null>(null);
  // Get sample documents, for missing samples: bytes so far while it downloads, and what went wrong.
  const [samplesFetch, setSamplesFetch] = useState<{
    receivedBytes: number;
    totalBytes: number;
  } | null>(null);
  const [samplesIssue, setSamplesIssue] = useState<string | null>(null);
  const performanceResult = useMemo(
    () => latestPerformanceMeasurement(history, details),
    [history, details],
  );
  const calibration =
    performanceCalibration === undefined ? storedCalibration : performanceCalibration;
  const performanceRating =
    performanceResult.kind === 'ready' && calibration
      ? measuredPerformanceScore(performanceResult.measurement, calibration)
      : null;
  const pageLive = useRef(true);
  const readAttempt = useRef(0);
  const availabilityRevision = useRef(0);
  const stateRevision = useRef(0);

  useEffect(() => {
    pageLive.current = true;
    return () => {
      pageLive.current = false;
      readAttempt.current += 1;
    };
  }, []);

  const setReadIssue = useCallback((key: ComputerReadIssueKey, message: string | null) => {
    if (!pageLive.current) return;
    setReadIssues((current) => {
      if (message === null) {
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
      }
      return current[key] === message ? current : { ...current, [key]: message };
    });
  }, []);

  const reload = useCallback(async () => {
    const attempt = ++readAttempt.current;
    const availabilityRequest = ++availabilityRevision.current;
    const stateRequest = ++stateRevision.current;
    const api = desktop();
    if (!api) {
      setReadIssue('history', 'Speed-check history is unavailable in this build.');
      setReadIssue('details', 'Saved speed-check details are unavailable in this build.');
      setReadIssue(
        'availability',
        'Paige cannot tell whether this computer is free for a speed check.',
      );
      setReadIssue('state', 'The current speed-check state is unavailable in this build.');
      if (pageLive.current) setLoading(false);
      return;
    }
    if (pageLive.current) setLoading(true);
    // Current state and availability are independent of slower history/detail
    // reads: they are all Run speed test needs, so it never waits on past results.
    // A retry owns its response, but a pushed state always supersedes that snapshot.
    void api
      .getSpeedCheckState()
      .then((next) => {
        if (
          !pageLive.current ||
          readAttempt.current !== attempt ||
          stateRevision.current !== stateRequest
        )
          return;
        setState(next);
        setReadIssue('state', null);
      })
      .catch((reason) => {
        if (
          pageLive.current &&
          readAttempt.current === attempt &&
          stateRevision.current === stateRequest
        )
          setReadIssue(
            'state',
            issueText(reason, 'The current speed-check state could not be read.'),
          );
      });
    void api
      .getSpeedCheckAvailability()
      .then((next) => {
        if (
          !pageLive.current ||
          readAttempt.current !== attempt ||
          availabilityRevision.current !== availabilityRequest
        )
          return;
        setAvailability(next);
        setReadIssue('availability', null);
      })
      .catch((reason) => {
        if (
          pageLive.current &&
          readAttempt.current === attempt &&
          availabilityRevision.current === availabilityRequest
        )
          setReadIssue(
            'availability',
            issueText(
              reason,
              'Paige could not tell whether this computer is free for a speed check.',
            ),
          );
      });
    const [runs, runDetails, calibrationResult, graphicsResult] = await Promise.allSettled([
      api.getBenchmarkHistory(),
      api.getSpeedCheckDetails(),
      api.getPerformanceCalibration?.() ?? Promise.resolve(null),
      api.getGraphicsMemoryAtTestStart?.() ?? Promise.resolve(null),
    ]);
    if (!pageLive.current || readAttempt.current !== attempt) return;
    // A failed or missing refresh cannot retain a previously trusted rating.
    setStoredCalibration(calibrationResult.status === 'fulfilled' ? calibrationResult.value : null);
    // Which apps held graphics memory when the latest finished test started; none read is no names.
    setGraphicsMemory(graphicsResult.status === 'fulfilled' ? graphicsResult.value : null);

    if (runs.status === 'fulfilled') {
      setHistory(runs.value);
      setReadIssue('history', null);
    } else
      setReadIssue('history', issueText(runs.reason, 'Speed-check history could not be read.'));

    if (runDetails.status === 'fulfilled') {
      setDetails(runDetails.value);
      setReadIssue('details', null);
    } else
      setReadIssue(
        'details',
        issueText(runDetails.reason, 'Saved speed-check details could not be read.'),
      );

    setLoading(false);
  }, [setReadIssue]);

  useEffect(() => {
    const api = desktop();
    if (!api) {
      setReadIssue('updates', 'Update status is unavailable in this build.');
      return;
    }
    let live = true;
    let observedRevision = 0;
    const stop = api.onUpdateStatusChanged((next) => {
      observedRevision += 1;
      if (!live || !pageLive.current) return;
      setUpdateStatus(next);
      setReadIssue('updates', null);
    });
    const requestRevision = observedRevision;
    void api
      .getUpdateStatus()
      .then((next) => {
        if (!live || !pageLive.current || observedRevision !== requestRevision) return;
        setUpdateStatus(next);
        setReadIssue('updates', null);
      })
      .catch((reason) => {
        if (live && observedRevision === requestRevision)
          setReadIssue('updates', issueText(reason, 'Update status could not be read.'));
      });
    return () => {
      live = false;
      stop();
    };
  }, [setReadIssue]);

  useEffect(() => {
    const api = desktop();
    if (!api) {
      setReadIssue('health', 'Graphics status is unavailable in this build.');
      return;
    }
    let live = true;
    let observedRevision = 0;
    const stop = api.onWorkstationHealthChanged((next) => {
      observedRevision += 1;
      if (!live || !pageLive.current) return;
      setHealth(next);
      setReadIssue('health', null);
    });
    const requestRevision = observedRevision;
    void api
      .getWorkstationHealth(false)
      .then((next) => {
        if (!live || !pageLive.current || observedRevision !== requestRevision) return;
        setHealth(next);
        setReadIssue('health', null);
      })
      .catch((reason) => {
        if (live && observedRevision === requestRevision)
          setReadIssue('health', issueText(reason, 'Graphics status could not be read.'));
      });
    return () => {
      live = false;
      stop();
    };
  }, [setReadIssue]);

  useEffect(() => {
    const api = desktop();
    if (!api) {
      void reload();
      setReadIssue('state', 'The current speed-check state is unavailable in this build.');
      return;
    }
    let live = true;
    const stop = api.onSpeedCheckStateChanged((next) => {
      stateRevision.current += 1;
      if (!live || !pageLive.current) return;
      setState(next);
      setReadIssue('state', null);
      // A finished or stopped check changes what there is to show, so the screen
      // refetches rather than waiting for the operator to navigate away and back.
      if (next.kind !== 'running') void reload();
    });
    void reload();
    return () => {
      live = false;
      stop();
    };
  }, [reload, setReadIssue]);

  // While a check is running the machine is busy by definition, so availability
  // is only re-polled when it is not.
  useEffect(() => {
    if (state?.kind === 'running') return;
    const timer = window.setInterval(() => {
      const api = desktop();
      if (!api) {
        setReadIssue(
          'availability',
          'Paige cannot tell whether this computer is free for a speed check.',
        );
        return;
      }
      const request = ++availabilityRevision.current;
      void api
        .getSpeedCheckAvailability()
        .then((next) => {
          if (!pageLive.current || availabilityRevision.current !== request) return;
          setAvailability(next);
          setReadIssue('availability', null);
        })
        .catch((reason) => {
          if (availabilityRevision.current === request)
            setReadIssue(
              'availability',
              issueText(
                reason,
                'Paige could not tell whether this computer is free for a speed check.',
              ),
            );
        });
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [setReadIssue, state?.kind]);

  const running = state?.kind === 'running';
  // `from`: which button started it (telemetry's speed-test.start); none, the start after Get sample documents.
  const start = (
    depth: SpeedCheckDepth,
    pauseImports?: boolean,
    from?: 'hint' | 'settings' | 'card',
  ) => {
    const api = desktop();
    if (!api) return;
    setStartIssue(null);
    setSamplesIssue(null);
    void api.startSpeedCheck(depth, pauseImports ? true : undefined, from).catch((reason) => {
      if (!pageLive.current) return;
      setStartIssue(issueText(reason, 'The computer speed test could not start.'));
      const api = desktop();
      if (!api) {
        setReadIssue('availability', issueText(reason, 'The speed check could not start.'));
        return;
      }
      const request = ++availabilityRevision.current;
      void api
        .getSpeedCheckAvailability()
        .then((next) => {
          if (!pageLive.current || availabilityRevision.current !== request) return;
          setAvailability(next);
          setReadIssue('availability', null);
        })
        .catch((availabilityReason) => {
          if (availabilityRevision.current === request)
            setReadIssue(
              'availability',
              issueText(
                availabilityReason,
                'Paige could not tell whether this computer is free for a speed check.',
              ),
            );
        });
    });
  };

  // Run speed test was refused for missing sample documents and this version can get them back:
  // Get sample documents downloads them (or copies them back when they are still on this computer),
  // then starts the test that was asked for. The card says so where the button was pressed.
  const samplesRoute: SpeedTestSamplesRoute | null = availability?.blockers.includes(
    'no-sample-documents',
  )
    ? (availability.samples ?? null)
    : null;
  // A test that stopped says why on the card, the same way a refused start does; when it stopped
  // for missing samples, Get sample documents puts them back and starts it again.
  const stopped = state?.kind === 'stopped' ? state : null;
  const stoppedForSamples = stopped?.cause === 'missing-samples' && !startIssue;
  const shownRoute = samplesRoute ?? (stoppedForSamples ? (stopped?.samples ?? null) : null);
  const offerSamples =
    (Boolean(startIssue?.startsWith(speedTestBlockedLine['no-sample-documents'])) ||
      stoppedForSamples) &&
    (shownRoute === 'download' || shownRoute === 'copy');
  const getSamples = () => {
    const api = desktop();
    if (!api?.getSpeedTestSamples || samplesFetch) return;
    setSamplesIssue(null);
    setSamplesFetch({ receivedBytes: 0, totalBytes: 0 });
    const stop = api.onSpeedTestSamplesProgress?.((progress) => {
      if (pageLive.current) setSamplesFetch(progress);
    });
    void api
      .getSpeedTestSamples()
      .then(
        () => {
          if (!pageLive.current) return;
          setStartIssue(null);
          const request = ++availabilityRevision.current;
          return api.getSpeedCheckAvailability().then(
            (next) => {
              if (!pageLive.current || availabilityRevision.current !== request) return;
              setAvailability(next);
              setReadIssue('availability', null);
              start('performance', !next.canRun);
            },
            () => {
              if (pageLive.current) void reload();
            },
          );
        },
        (reason) => {
          if (pageLive.current)
            setSamplesIssue(issueText(reason, 'The sample documents could not be put back.'));
        },
      )
      .finally(() => {
        stop?.();
        if (pageLive.current) setSamplesFetch(null);
      });
  };
  // Imports pause for the test only when they are all that is in the way.
  const importsWillPause = Boolean(
    availability &&
    !availability.canRun &&
    availability.blockers.length > 0 &&
    availability.blockers.every((blocker) => speedTestPausableBlockers.includes(blocker)),
  );

  // The speed test hint's Run speed test opens Performance and starts the test here, where its
  // progress and Stop are: the same start as the button, once the state and availability are
  // read, and once per request. A test already running just shows its progress.
  const startedRequest = useRef(0);
  useEffect(() => {
    if (
      !speedTestRequest ||
      startedRequest.current === speedTestRequest ||
      section !== 'performance'
    )
      return;
    if (!running && !readIssues.state && !readIssues.availability && (!state || !availability))
      return;
    startedRequest.current = speedTestRequest;
    onSpeedTestRequestHandled?.();
    if (!running && state && availability && !readIssues.state && !readIssues.availability)
      start('performance', !availability.canRun, 'hint');
  });

  // What may have slowed a low score, from the why its test kept (speed-test-why.ts): plain
  // things the person can relate to, never how the test works.
  const whyLines = useMemo(() => {
    if (performanceResult.kind !== 'ready') return [];
    const run = history.find((entry) => entry.run.runId === performanceResult.runId)?.run;
    return speedTestWhyLines(
      details.find((detail) => detail.runId === performanceResult.runId)?.why,
      {
        score: performanceRating,
        graphicsCard: Boolean(run?.hardware.gpus.some((gpu) => gpu.vendor === 'nvidia')),
      },
    );
  }, [details, history, performanceRating, performanceResult]);

  // Two sides under the score: this computer's own parts, from its latest speed test record and
  // the computer check, beside the job's skills and the training schedule (speed-advice.ts).
  const latestHardware = useMemo(
    () =>
      [...history].sort(
        (left, right) => Date.parse(right.run.capturedAt) - Date.parse(left.run.capturedAt),
      )[0]?.run.hardware ?? null,
    [history],
  );
  const skills = useJobSkillCounts(
    section === 'performance' && activeJob && !activeJob.settingUp ? activeJob.id : null,
  );
  const adviceFacts: SpeedAdviceFacts = {
    boost: readIssues.health ? 'unknown' : speedBoostSummary(health).mode,
    // The card Speed boost reads on; otherwise every adapter the check found ([] is an absence
    // only when it could look), then the test record's.
    cards: speedBoostCards(health).length
      ? speedBoostCards(health)
      : health?.graphicsCards?.length
        ? health.graphicsCards
        : latestHardware?.gpus.length
          ? latestHardware.gpus.map((gpu) => gpu.model)
          : health && typeof health.speedBoostHardwareKey === 'string'
            ? []
            : null,
    memoryShort: speedBoostMemoryShort(health),
    processor: latestHardware?.cpu.model ?? null,
    memoryMegabytes: latestHardware?.memory.totalMb ?? null,
    job: activeJob ? { id: activeJob.id, settingUp: activeJob.settingUp } : null,
    skills,
    training,
    // Only beside the score it was taken for: main keeps a reading per finished test, with its run.
    graphicsMemory:
      performanceResult.kind === 'ready' && graphicsMemory?.runId === performanceResult.runId
        ? graphicsMemory
        : null,
  };
  // "Email me this report" (shared/email-asks.ts): the card's own facts for this computer, built at
  // Send. Never an app's name: Free graphics memory goes as enough or short only.
  const emailAsks = useEmailAsks();
  const speedReport = (): SpeedReportFacts | null => {
    if (performanceResult.kind !== 'ready' || performanceRating === null) return null;
    const cards = adviceFacts.cards === null ? null : cardNames(adviceFacts.cards);
    const skills =
      adviceFacts.job && adviceFacts.skills && adviceFacts.skills.total > 0
        ? { ready: adviceFacts.skills.ready, total: adviceFacts.skills.total }
        : null;
    return {
      testedUtc: performanceResult.capturedAt,
      score: Math.round(performanceRating),
      why: whyLines.map((line) => speedReportWhyCode(line.icon)),
      speedBoost: adviceFacts.boost,
      graphicsCards: cards,
      processor: processorName(adviceFacts.processor),
      memoryGb:
        adviceFacts.memoryMegabytes && adviceFacts.memoryMegabytes > 0
          ? Math.max(1, Math.round(adviceFacts.memoryMegabytes / 1024))
          : null,
      skills,
      training: adviceFacts.training
        ? {
            on: adviceFacts.training.on,
            start: adviceFacts.training.on ? (adviceFacts.training.start ?? null) : null,
            end: adviceFacts.training.on ? (adviceFacts.training.end ?? null) : null,
          }
        : null,
      freeGraphicsMemory: freeGraphicsMemoryState(adviceFacts),
    };
  };
  const reportTestedUtc =
    performanceResult.kind === 'ready' && performanceRating !== null
      ? performanceResult.capturedAt
      : null;
  const showSpeedBoost = () => {
    const target = pageRef.current?.querySelector<HTMLButtonElement>('.boost-info');
    target?.focus({ preventScroll: true });
    const boostSection =
      target?.closest('.speed-boost-section') ??
      pageRef.current?.querySelector('.workstation-health');
    boostSection?.scrollIntoView({ block: 'nearest' });
  };
  const adviceAction = (action: AdviceActionId) => {
    if (action === 'speed-boost') showSpeedBoost();
    else if (activeJob) (action === 'teach' ? onTeach : onTrain)?.(activeJob.id);
  };

  // The header facts are doorways into the local sections. Switching one
  // focused section preserves the page state and avoids a second scroll context.
  const lastRun = history[0] ?? null;
  const lastRunRate = lastRun ? overallPagesPerMinute(lastRun) : null;
  const lastRunWhen = lastRun ? new Date(lastRun.run.capturedAt) : null;
  const versionLine = readIssues.updates
    ? 'Update status could not be read'
    : (checkNote ??
      (updateStatus?.state === 'disabled'
        ? 'Release channel'
        : updateStatus
          ? updateActivityLine(updateStatus)
          : "Reading this computer's update setting"));
  // `failed` needs eyes on it: a check that did not finish wearing the settled
  // gray read as "nothing to report", which is the opposite of its news.
  const versionTone =
    readIssues.updates ||
    updateStatus?.state === 'ready' ||
    updateStatus?.state === 'downloading' ||
    updateStatus?.state === 'failed'
      ? 'attention'
      : checking || updateStatus?.state === 'checking'
        ? 'working'
        : 'settled';
  const graphics = speedBoostSummary(health).mode;
  const graphicsLine = readIssues.health
    ? 'Computer status could not be read'
    : computerCheckMessage(health).title;
  const speedMeasured =
    lastRunRate !== null && lastRunWhen !== null && !Number.isNaN(lastRunWhen.getTime());
  // Never-run is the absence of an answer, not a settled one, and the fact
  // doubles as the invitation: it says what accepting would cost.
  const speedTone = readIssues.history
    ? 'attention'
    : running
      ? 'working'
      : speedMeasured
        ? 'settled'
        : loading
          ? 'working'
          : 'neutral';
  const graphicsTone = readIssues.health
    ? 'attention'
    : !health
      ? 'working'
      : graphics === 'attention' || graphics === 'partial'
        ? 'attention'
        : graphics === 'on' || graphics === 'off'
          ? 'settled'
          : 'neutral';
  const failedConnections = connections.filter((item) => item.lastFailureCode);
  const checkedConnections = connections.filter(
    (item) => !item.lastFailureCode && item.lastSuccessAt,
  );
  const connectionsReady =
    connections.length > 0 && checkedConnections.length === connections.length;
  const connectionsNeedAttention = Boolean(connectionIssue) || failedConnections.length > 0;
  const updatesAttention =
    Boolean(readIssues.updates) ||
    updateStatus?.state === 'ready' ||
    updateStatus?.state === 'failed';
  // Owner-chosen order (September 23, 2026): how it looks, how fast it reads,
  // where documents live and go, then the license and updates.
  const sections: readonly WorkspaceSectionItem<SettingsSection>[] = hostSettingsSections([
    { id: 'appearance', label: 'Appearance', tone: 'neutral' },
    {
      id: 'performance',
      label: 'Performance',
      badge:
        readIssues.health || readIssues.history || readIssues.state || readIssues.availability
          ? 'Attention'
          : graphics === 'attention' || graphics === 'partial'
            ? 'Attention'
            : running
              ? 'Running'
              : undefined,
      tone:
        readIssues.health ||
        readIssues.history ||
        readIssues.state ||
        readIssues.availability ||
        graphics === 'attention' ||
        graphics === 'partial'
          ? 'attention'
          : speedMeasured && (graphics === 'on' || graphics === 'off')
            ? 'ready'
            : 'neutral',
    },
    ...(clusterContent ? [{ id: 'cluster' as const, label: 'Shared processing' }] : []),
    { id: 'storage', label: 'Storage' },
    {
      id: 'connections',
      label: 'Connections',
      badge: connectionsNeedAttention ? 'Attention' : undefined,
      tone: connectionsNeedAttention ? 'attention' : connectionsReady ? 'ready' : 'neutral',
    },
    { id: 'license', label: 'License' },
    {
      id: 'updates',
      label: 'Updates',
      badge:
        updateStatus?.state === 'ready' ? 'Update' : updatesAttention ? 'Attention' : undefined,
      tone: updatesAttention || Boolean(readIssues.updates) ? 'attention' : 'neutral',
    },
    // Last, where someone on the phone with Ademero is told to look (ADR-0088).
    { id: 'support', label: 'Support' },
  ]);

  const chooseAppearance = async (mode: AppearanceMode) => {
    if (mode === appearance || appearanceChanging) return;
    setAppearanceFailure('');
    try {
      await onAppearance(mode);
    } catch (reason) {
      setAppearanceFailure(
        reason instanceof Error
          ? reason.message
          : 'Paige could not save this appearance on this computer.',
      );
    }
  };

  return (
    <div ref={pageRef} className="page speed-check-page settings-page">
      {!standalone && (
        <header className="settings-heading">
          <h1>Settings</h1>
          {hostHas('local-storage') && <span>This computer</span>}
        </header>
      )}
      <div className="settings-layout">
        {!standalone && (
          <WorkspaceSectionNav
            label="Settings sections"
            items={sections}
            active={section}
            onChange={openSection}
            telemetry="settings-nav"
          />
        )}
        <div className="settings-content">
          {section === 'cluster' && clusterContent}
          {section === 'jobs' && (
            <section className="settings-job-library">
              {jobsContent ?? (
                <button className="primary-button" onClick={onOpenJobs}>
                  Open scan jobs
                </button>
              )}
            </section>
          )}
          {section === 'overview' && (
            <section className="settings-destinations" aria-label="Settings overview">
              <button
                type="button"
                data-settings-destination="performance"
                className={`settings-destination ${graphicsTone === 'attention' || speedTone === 'attention' ? 'attention' : graphicsTone}`}
                onClick={() => openSection('performance')}
              >
                <span className="settings-destination-icon">
                  <Cpu size={24} aria-hidden="true" />
                </span>
                <span className="settings-destination-copy">
                  <strong>Performance</strong>
                  <span>{graphicsLine}</span>
                  <small>
                    {readIssues.history
                      ? 'Speed-check history could not be read'
                      : running
                        ? 'Speed check in progress'
                        : speedMeasured
                          ? `Last check: ${formatPagesPerMinute(lastRunRate!)} · ${lastRunWhen!.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                          : loading
                            ? 'Reading speed-check history…'
                            : 'Reader choice and speed checks'}
                  </small>
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                data-settings-destination="license"
                className="settings-destination"
                onClick={() => openSection('license')}
              >
                <span className="settings-destination-icon">
                  <LockKeyhole size={24} aria-hidden="true" />
                </span>
                <span className="settings-destination-copy">
                  <strong>License</strong>
                  <span>Trial time and activation</span>
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                data-settings-destination="connections"
                className={`settings-destination ${connectionsNeedAttention ? 'attention' : ''}`}
                onClick={() => openSection('connections')}
              >
                <span className="settings-destination-icon">
                  <Database size={24} aria-hidden="true" />
                </span>
                <span className="settings-destination-copy">
                  <strong>Connections</strong>
                  <span>
                    {connectionsLoading
                      ? 'Reading saved connections'
                      : connectionIssue
                        ? connectionIssueSummary[connectionIssue.context]
                        : connections.length === 0
                          ? 'No external systems connected'
                          : failedConnections.length > 0
                            ? `${failedConnections.length} ${failedConnections.length === 1 ? 'connection needs' : 'connections need'} attention`
                            : connectionsReady
                              ? `${connections.length} ${connections.length === 1 ? 'connection' : 'connections'} ready`
                              : `${checkedConnections.length} of ${connections.length} checked`}
                  </span>
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                data-settings-destination="storage"
                className="settings-destination"
                onClick={() => openSection('storage')}
              >
                <span className="settings-destination-icon">
                  <HardDrive size={24} aria-hidden="true" />
                </span>
                <span className="settings-destination-copy">
                  <strong>Storage</strong>
                  <span>Where your documents are kept</span>
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                data-settings-destination="appearance"
                className="settings-destination"
                onClick={() => openSection('appearance')}
              >
                <span className="settings-destination-icon">
                  <Palette size={24} aria-hidden="true" />
                </span>
                <span className="settings-destination-copy">
                  <strong>Appearance</strong>
                  <span>
                    {appearance === 'dark' ? 'Dark' : 'Light'} · App text and controls
                    {interfaceZoom ? ` ${interfaceZoom.percent}%` : ''}
                  </span>
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                data-settings-destination="updates"
                className={`settings-destination ${versionTone}`}
                onClick={() => openSection('updates')}
              >
                <span className="settings-destination-icon">
                  {checking ? (
                    <LoaderCircle className="spin" size={24} aria-hidden="true" />
                  ) : (
                    <RefreshCw size={24} aria-hidden="true" />
                  )}
                </span>
                <span className="settings-destination-copy">
                  <strong>Updates</strong>
                  <span>{versionLine}</span>
                  <small>{appVersion ? `Paige ${appVersion}` : 'Installed software'}</small>
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            </section>
          )}

          {section === 'performance' && (
            <div
              className="computer-section performance-workspace"
              aria-labelledby="computer-performance-title"
            >
              <header className="computer-local-heading performance-heading">
                <div className="performance-intro">
                  <h2 id="computer-performance-title">
                    <span>Computer speed test</span>
                    <TooltipButton
                      type="button"
                      className="icon-button"
                      helpOnClick
                      aria-label="About the computer speed test"
                      tooltip="Measures this computer, not what your jobs learned."
                    >
                      <CircleHelp size={17} aria-hidden="true" />
                    </TooltipButton>
                  </h2>
                  <p>
                    {availability?.checksGraphicsCard
                      ? 'About six minutes. Checks your graphics card, then measures document-processing speed with this computer’s hardware and system settings.'
                      : 'About three minutes. Measure document-processing speed with this computer’s hardware and system settings.'}
                  </p>
                  {state?.kind === 'running' && (
                    <RunningCard
                      state={state}
                      onStop={() =>
                        desktop()?.cancelSpeedCheck() ?? Promise.reject(new Error('Unavailable'))
                      }
                    />
                  )}
                </div>
                <div className="performance-score-actions">
                  <PerformanceScore
                    score={performanceRating}
                    // PAIGE: no "Tested" line: the score's characters and their art are this
                    // computer's speed test, not copied (docs/runbooks/capturepoint-catch-up.md).
                    running={
                      !readIssues.state &&
                      state?.kind === 'running' &&
                      state.progress.depth === 'performance'
                    }
                    status={
                      loading
                        ? 'loading'
                        : readIssues.history ||
                            readIssues.details ||
                            readIssues.state ||
                            performanceResult.kind === 'unavailable'
                          ? 'unavailable'
                          : performanceResult.kind === 'ready' && performanceRating === null
                            ? 'unrated'
                            : undefined
                    }
                    detail={
                      readIssues.history || readIssues.details
                        ? 'Saved test could not be read.'
                        : performanceResult.kind === 'unavailable'
                          ? 'This saved test cannot be scored. Run a new performance test.'
                          : performanceResult.kind === 'ready' && performanceRating === null
                            ? 'This test cannot be compared with the current reference.'
                            : undefined
                    }
                  />
                  {!running && (
                    <button
                      type="button"
                      data-telemetry="speed-test-run"
                      className="primary-button performance-run-action"
                      disabled={
                        !state ||
                        !availability ||
                        Boolean(readIssues.state || readIssues.availability) ||
                        Boolean(samplesFetch)
                      }
                      onClick={() => {
                        if (state && availability && !readIssues.state && !readIssues.availability)
                          start('performance', !availability.canRun, 'settings');
                      }}
                    >
                      <Gauge size={18} aria-hidden="true" /> Run speed test
                    </button>
                  )}
                  {!running && (samplesFetch || samplesIssue || startIssue || stopped) ? (
                    <SpeedTestStartIssue
                      issue={samplesIssue ?? startIssue ?? stopped?.reason ?? null}
                      fetching={samplesFetch}
                      route={shownRoute}
                      kind={samplesIssue ? 'samples-failed' : startIssue ? 'refused' : 'stopped'}
                      offer={samplesIssue ? 'retry' : offerSamples ? 'samples' : null}
                      onGetSamples={getSamples}
                    />
                  ) : (
                    !running &&
                    importsWillPause && (
                      <small className="performance-pause-note">
                        Imports will pause for the test.
                      </small>
                    )
                  )}
                </div>
                {!running && reportTestedUtc && emailAsks?.available && (
                  <EmailAsk
                    kind="speed-report"
                    view={emailAsks}
                    className="email-ask-speed-report"
                    done={
                      emailAsks.speedReport &&
                      Date.parse(emailAsks.speedReport.testedUtc) === Date.parse(reportTestedUtc)
                        ? emailAsks.speedReport
                        : null
                    }
                    details={() => {
                      const report = speedReport();
                      return report ? { report } : {};
                    }}
                  />
                )}
                {!running && whyLines.length > 0 && <SpeedTestWhyList lines={whyLines} />}
                {state?.kind !== 'running' && (
                  <SpeedAdvice facts={adviceFacts} onAction={adviceAction} />
                )}
              </header>
              {(readIssues.state || readIssues.availability) && (
                <ComputerReadFailure
                  code="speed-test-load-failed"
                  title="Test availability could not be checked"
                  detail={readIssues.state ?? readIssues.availability!}
                  onRetry={() => void reload()}
                />
              )}
              <WorkstationHealthPanel embedded />
              <JobSpeedCard
                onOpenJobs={() => onSectionChange('jobs')}
                onPerformanceTest={() => start('performance', true, 'card')}
              />
            </div>
          )}

          {section === 'license' && <LicenseSettings />}

          {section === 'storage' && (
            <div className="computer-section" aria-labelledby="computer-storage-title">
              <header className="computer-local-heading">
                <div>
                  <h2 id="computer-storage-title">Storage</h2>
                </div>
              </header>
              <StorageLocationPanel />
            </div>
          )}

          {section === 'connections' && (
            <div className="computer-section" aria-labelledby="computer-connections-title">
              <header className="computer-local-heading">
                <div>
                  <h2 id="computer-connections-title">Connections</h2>
                </div>
              </header>
              <ConnectionManagement state={connectionState} />
            </div>
          )}

          {section === 'appearance' && (
            <div className="computer-section" aria-labelledby="settings-appearance-title">
              <header className={standalone ? 'visually-hidden' : 'computer-local-heading'}>
                <div>
                  <h2 id="settings-appearance-title">Appearance</h2>
                </div>
              </header>
              <section
                className="card appearance-settings-card"
                aria-busy={appearanceChanging || undefined}
              >
                <div className="appearance-options" role="radiogroup" aria-label="Appearance">
                  {(['light', 'dark'] as const).map((mode) => {
                    const selectedMode = appearance === mode;
                    const label = mode === 'light' ? 'Light' : 'Dark';
                    return (
                      <label
                        key={mode}
                        className={`appearance-choice ${selectedMode ? 'selected' : ''}`}
                      >
                        <input
                          className="visually-hidden"
                          type="radio"
                          name="capturepoint-appearance"
                          data-telemetry={mode === 'light' ? 'theme-light' : 'theme-dark'}
                          value={mode}
                          checked={selectedMode}
                          disabled={appearanceChanging}
                          onChange={() => void chooseAppearance(mode)}
                        />
                        <span className={`appearance-preview ${mode}`} aria-hidden="true">
                          <span className="appearance-preview-rail">
                            <i />
                            <i />
                            <i />
                          </span>
                          <span className="appearance-preview-page">
                            <i />
                            <i />
                            <i />
                          </span>
                        </span>
                        <span className="appearance-choice-label">
                          {mode === 'light' ? <Sun size={17} /> : <Moon size={17} />}
                          <strong>{label}</strong>
                          {selectedMode && (
                            <CheckCircle2
                              size={17}
                              className="appearance-selected"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <AccentSettings />
                <HintSettings />
                <TooltipSettings />
                {hostHas('interface-zoom') && (
                  <div className="interface-size-setting">
                    <span className="interface-size-setting-copy">
                      <strong>Text and control size</strong>
                      <small>Document zoom stays separate.</small>
                    </span>
                    {interfaceZoom ? (
                      <div
                        className="interface-size-controls"
                        role="group"
                        aria-label="App text and controls"
                      >
                        <TooltipButton
                          type="button"
                          disabled={interfaceZoom.atMinimum}
                          onClick={() => void onInterfaceZoom('out')}
                          aria-label="Make app text and controls smaller"
                          tooltip="Smaller"
                        >
                          <Minus size={15} />
                        </TooltipButton>
                        <output aria-live="polite">{interfaceZoom.percent}%</output>
                        <TooltipButton
                          type="button"
                          disabled={interfaceZoom.atMaximum}
                          onClick={() => void onInterfaceZoom('in')}
                          aria-label="Make app text and controls larger"
                          tooltip="Larger"
                        >
                          <Plus size={15} />
                        </TooltipButton>
                        <button
                          type="button"
                          className="interface-size-reset"
                          disabled={interfaceZoom.level === 0}
                          onClick={() => void onInterfaceZoom('reset')}
                        >
                          Default size
                        </button>
                        {interfaceZoom.atMaximum && (
                          <small className="interface-size-limit">
                            Maximum on this monitor: {interfaceZoom.maximumPercent}%
                          </small>
                        )}
                      </div>
                    ) : (
                      <span className="appearance-saving" role="status">
                        <LoaderCircle className="spin" size={14} /> Reading size…
                      </span>
                    )}
                  </div>
                )}
                {appearanceChanging && (
                  <span className="appearance-saving" role="status">
                    <LoaderCircle className="spin" size={14} /> Saving appearance…
                  </span>
                )}
                {appearanceFailure && (
                  <div
                    className="appearance-error"
                    data-telemetry="error:appearance-save-failed"
                    role="alert"
                  >
                    <TriangleAlert size={15} />
                    <span>{appearanceFailure}</span>
                  </div>
                )}
              </section>
            </div>
          )}

          {section === 'updates' && (
            <div className="computer-section" aria-labelledby="settings-updates-title">
              <header className="computer-local-heading">
                <div>
                  <h2 id="settings-updates-title">Updates</h2>
                </div>
              </header>
              <SoftwareUpdatesCard
                appVersion={appVersion}
                onCheckForUpdate={onCheckForUpdate}
                checking={checking}
                checkNote={checkNote}
              />
            </div>
          )}

          {section === 'support' && (
            <div className="computer-section" aria-labelledby="settings-support-title">
              <header className="computer-local-heading">
                <div>
                  <h2 id="settings-support-title">Support</h2>
                </div>
              </header>
              <SupportAccessCard />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Why Run speed test did not start, on the card under the button (never a banner): the plain line
 * main gave for what is in the way, with Technical details when it carries any. For missing sample
 * documents that this version can get back, Get sample documents (it downloads them, or copies them
 * back when they are still on this computer), then its progress, or its failure with Try again.
 */
function SpeedTestStartIssue({
  issue,
  kind,
  fetching,
  route,
  offer,
  onGetSamples,
}: {
  issue: string | null;
  /** What the line is about, for its message code: a refused start, Get sample documents failing, or a stopped test (no code). */
  kind: 'refused' | 'samples-failed' | 'stopped';
  fetching: { receivedBytes: number; totalBytes: number } | null;
  route: SpeedTestSamplesRoute | null;
  offer: 'samples' | 'retry' | null;
  onGetSamples: () => void;
}) {
  if (fetching) {
    const megabytes = (bytes: number) => Math.max(0, Math.round(bytes / 1_000_000));
    const total = megabytes(fetching.totalBytes);
    const line =
      fetching.totalBytes > 0
        ? `Downloading sample documents… ${Math.min(total, megabytes(fetching.receivedBytes))} of ${total} MB`
        : route === 'copy'
          ? 'Copying sample documents…'
          : 'Downloading sample documents…';
    const percent =
      fetching.totalBytes > 0
        ? Math.min(100, (fetching.receivedBytes / fetching.totalBytes) * 100)
        : 0;
    return (
      <div
        key="fetching"
        className="performance-start-issue working"
        role="status"
        aria-live="polite"
      >
        <span>
          <LoaderCircle className="spin" size={13} aria-hidden="true" /> {line}
        </span>
        {fetching.totalBytes > 0 && (
          <span className="performance-start-meter" aria-hidden="true">
            <span style={{ width: `${percent}%` }} />
          </span>
        )}
      </div>
    );
  }
  if (!issue) return null;
  // Each line is its own element, so telemetry sees each one once: a refused start or a failed fetch by its code.
  return (
    <div
      key={`${kind}:${issue}`}
      className="performance-start-issue"
      role="alert"
      data-telemetry={
        kind === 'refused'
          ? 'error:speed-test-refused'
          : kind === 'samples-failed'
            ? 'error:speed-test-failed'
            : undefined
      }
    >
      <OperatorErrorText message={issue} />
      {offer && (
        <button
          type="button"
          data-telemetry={offer === 'retry' ? 'speed-test-samples-retry' : 'speed-test-get-samples'}
          className="secondary-button performance-start-fix"
          onClick={onGetSamples}
        >
          {offer === 'retry' ? (
            <>
              <RotateCw size={14} aria-hidden="true" /> Try again
            </>
          ) : (
            <>
              <Download size={14} aria-hidden="true" /> Get sample documents
            </>
          )}
        </button>
      )}
    </div>
  );
}

const whyIcons: Record<SpeedTestWhyLine['icon'], typeof Cpu> = {
  apps: AppWindow,
  processor: Cpu,
  person: MousePointer2,
  card: Gpu,
};

/** Under a low score: what may have slowed it, one plain line each, from the test's own record. */
function SpeedTestWhyList({ lines }: { lines: readonly SpeedTestWhyLine[] }) {
  const headingId = useId();
  return (
    <div className="speed-test-why">
      <span id={headingId}>{SPEED_TEST_WHY_HEADING}</span>
      <ul aria-labelledby={headingId}>
        {lines.map((line) => {
          const Icon = whyIcons[line.icon];
          return (
            <li key={line.text}>
              <Icon size={15} aria-hidden="true" />
              <span>{line.text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ComputerReadFailure({
  code,
  title,
  detail,
  actionLabel = 'Try again',
  onRetry,
}: {
  code: TelemetryUiErrorCode;
  title: string;
  detail: string;
  actionLabel?: string;
  onRetry: () => void;
}) {
  return (
    <div className="speed-read-failure" role="alert" data-telemetry={`error:${code}`}>
      <TriangleAlert size={16} aria-hidden="true" />
      <span>
        <strong>{title}</strong>
        <small>{detail}</small>
      </span>
      <button type="button" className="quiet-button small" onClick={onRetry}>
        <RotateCw size={13} /> {actionLabel}
      </button>
    </div>
  );
}

/**
 * The speed of a real job, batch over batch, from the job's own history — the
 * learned setup measuring itself. No synthetic run can say this: these are the
 * batches the operator actually fed, timed by the pipeline that read them, with
 * the share whose document type came from learning shown beside each. That is
 * type provenance only; measured throughput—not this share—states the speed.
 */
const measureStages = [
  'preparing',
  'recognizing',
  'organizing',
  'understanding',
  'finalizing',
] as const;

/**
 * A Measure now still running when the card goes away (Settings closed, another section opened):
 * the next card picks it up, with its clock, its latest step and its result, instead of a second
 * click being refused while the first one's answer is thrown away. A measurement that finishes
 * while no card is open stays here, settled, until a card has shown its answer, and says when it was taken.
 */
type MeasurementInFlight = {
  jobId: string;
  startedAt: number;
  progress: FolderAnalysisProgress | null;
  result: Promise<JobSpeedMeasurement>;
  settled: boolean;
  /** When the answer arrived (Date.now()), or null while it runs. */
  settledAt: number | null;
};
let measurementInFlight: MeasurementInFlight | null = null;
/** An answer shown later than this after it arrived is labelled with its time, not "now". */
const MEASURED_NOW_MS = 60_000;
/** "2:14 PM" today, else "Sep 20, 2:14 PM". */
const measuredTime = (at: number) => {
  const when = new Date(at);
  return when.toDateString() === new Date().toDateString()
    ? when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : when.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

function beginMeasurement(
  api: NonNullable<ReturnType<typeof desktop>>,
  job: JobDefinition,
): MeasurementInFlight {
  // The request starts on the next tick, so the progress subscription below is already listening.
  const record: MeasurementInFlight = {
    jobId: job.id,
    startedAt: Date.now(),
    progress: null,
    settled: false,
    settledAt: null,
    result: Promise.resolve()
      .then(() => api.measureJobNow(job))
      .finally(() => {
        stop();
        record.settled = true;
        record.settledAt = Date.now();
      }),
  };
  const stop = api.onMeasureJobProgress((progress) => {
    if (progress.jobId === record.jobId) record.progress = progress;
  });
  measurementInFlight = record;
  return record;
}

function JobSpeedCard({
  onOpenJobs,
  onPerformanceTest,
}: {
  onOpenJobs: () => void;
  onPerformanceTest: () => void;
}) {
  const [jobs, setJobs] = useState<JobDefinition[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState<string | null>(null);
  // Taken once, at the first render: a measurement that settles before the mount effect runs is
  // still this card's to show, not a "Measuring…" with nothing behind it.
  const [adopted] = useState(() => measurementInFlight);
  const [jobId, setJobId] = useState<string>(() => adopted?.jobId ?? '');
  const [speedHistory, setSpeedHistory] = useState<JobSpeedHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyRetry, setHistoryRetry] = useState(0);
  const [measuring, setMeasuring] = useState(() => adopted !== null);
  const [checkingMeasurement, setCheckingMeasurement] = useState(false);
  const [measured, setMeasured] = useState<JobSpeedMeasurement | null>(null);
  /** When `measured` arrived, if it was shown well after that (a card that opened later picked it up); else null ("now"). */
  const [measuredEarlierAt, setMeasuredEarlierAt] = useState<number | null>(null);
  const [measureError, setMeasureError] = useState<string | null>(null);
  const [queueBlockers, setQueueBlockers] = useState<string[]>([]);
  const [queueBlocked, setQueueBlocked] = useState(false);
  const [measureProgress, setMeasureProgress] = useState<FolderAnalysisProgress | null>(
    () => adopted?.progress ?? null,
  );
  const [measureStartedAt, setMeasureStartedAt] = useState<number | null>(
    () => adopted?.startedAt ?? null,
  );
  // The measurement whose answer this card has put on screen; once painted, it is no longer kept.
  const [shownMeasurement, setShownMeasurement] = useState<MeasurementInFlight | null>(null);
  // ONE BAR FOR THE WHOLE MEASUREMENT. The engine counts each stage from zero
  // (pages, then documents), so the bar went full and back to empty at every stage
  // (owner, 2026-09-29: progress never visibly goes backward). The bar is the
  // furthest point reached across the five stages; each measurement starts its own.
  const measureShown = useRef<{ run: number | null; rank: number; fraction: number }>({
    run: null,
    rank: -1,
    fraction: 0,
  });
  if (measureShown.current.run !== measureStartedAt)
    measureShown.current = { run: measureStartedAt, rank: -1, fraction: 0 };
  if (measureProgress) {
    const rank = measureStages.indexOf(measureProgress.stage as (typeof measureStages)[number]);
    const within =
      measureProgress.total > 0
        ? Math.min(1, Math.max(0, measureProgress.completed / measureProgress.total))
        : 0;
    const reached =
      measureProgress.stage === 'complete'
        ? 1
        : rank >= 0
          ? (rank + within) / measureStages.length
          : 0;
    measureShown.current = {
      run: measureStartedAt,
      rank: Math.max(measureShown.current.rank, rank),
      fraction: Math.max(measureShown.current.fraction, reached),
    };
  }
  // A late report from an earlier stage is not shown as a step back either.
  const measureCurrent =
    measureProgress &&
    measureStages.indexOf(measureProgress.stage as (typeof measureStages)[number]) >=
      measureShown.current.rank;
  const measureStage =
    measureShown.current.rank >= 0 ? measureStages[measureShown.current.rank] : null;
  const [, tick] = useState(0);
  const cardLive = useRef(true);
  const selectedJob = useRef(jobId);
  selectedJob.current = jobId;
  const measurementOwner = useRef<{ jobId: string } | null>(null);
  const jobsRead = useRef(0);

  useEffect(() => {
    cardLive.current = true;
    return () => {
      cardLive.current = false;
      measurementOwner.current = null;
      jobsRead.current += 1;
    };
  }, []);

  useEffect(() => {
    if (!measuring) return;
    // The elapsed clock and the pipeline's own step events. A wait with no
    // progress is never acceptable — the operator must always see what is
    // happening and how far along it is.
    const timer = window.setInterval(() => tick((t) => t + 1), 1_000);
    const unsubscribe = desktop()?.onMeasureJobProgress((progress) => {
      if (
        cardLive.current &&
        measurementOwner.current?.jobId === progress.jobId &&
        selectedJob.current === progress.jobId
      )
        setMeasureProgress(progress);
    });
    return () => {
      window.clearInterval(timer);
      unsubscribe?.();
    };
  }, [measuring]);

  const loadJobs = useCallback(async () => {
    const request = ++jobsRead.current;
    const api = desktop();
    setJobsLoading(true);
    setJobsError(null);
    if (!api) {
      setJobsLoading(false);
      setJobsError('Jobs are unavailable in this build.');
      return;
    }
    try {
      const list = await api.listJobs();
      if (!cardLive.current || jobsRead.current !== request) return;
      setJobs(list);
      setJobId((current) =>
        current && list.some((job) => job.id === current) ? current : (list[0]?.id ?? ''),
      );
    } catch (reason) {
      if (cardLive.current && jobsRead.current === request)
        setJobsError(issueText(reason, 'Jobs could not be read.'));
    } finally {
      if (cardLive.current && jobsRead.current === request) setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);
  useEffect(() => {
    if (!jobId) return;
    let live = true;
    setSpeedHistory((current) => (current?.jobId === jobId ? current : null));
    setHistoryLoading(true);
    setHistoryError(null);
    setMeasured(null);
    setMeasureError(null);
    setQueueBlocked(false);
    setQueueBlockers([]);
    const api = desktop();
    if (!api) {
      setSpeedHistory(null);
      setHistoryLoading(false);
      setHistoryError('Job speed history is unavailable in this build.');
      return;
    }
    void api
      .getJobSpeedHistory(jobId)
      .then((next) => {
        if (!live) return;
        if (next.jobId !== jobId)
          throw new Error('The returned speed history belongs to a different job. Try again.');
        setSpeedHistory(next);
        setHistoryError(null);
      })
      .catch((reason) => {
        if (live) setHistoryError(issueText(reason, "This job's speed history could not be read."));
      })
      .finally(() => {
        if (live) setHistoryLoading(false);
      });
    return () => {
      live = false;
    };
  }, [historyRetry, jobId]);

  // Ownership ends with the measurement it was taken for, or with the card.
  const release = (owner: { jobId: string }) => {
    if (measurementOwner.current !== owner) return;
    measurementOwner.current = null;
    if (cardLive.current) {
      setMeasuring(false);
      setCheckingMeasurement(false);
    }
  };
  // One measurement's answer on this card, while the card still shows its job.
  const follow = async (record: MeasurementInFlight, owner: { jobId: string }) => {
    const owns = () =>
      cardLive.current && measurementOwner.current === owner && selectedJob.current === owner.jobId;
    try {
      const result = await record.result;
      if (!owns()) return;
      if (result.jobId !== owner.jobId)
        throw new Error(
          'The returned measurement belongs to a different job. Nothing is shown for this job; try again.',
        );
      // An answer kept while no card was open may be hours old: it says when it was measured.
      setMeasuredEarlierAt(
        record.settledAt !== null && Date.now() - record.settledAt > MEASURED_NOW_MS
          ? record.settledAt
          : null,
      );
      setMeasured(result);
    } catch (reason) {
      if (owns())
        setMeasureError(
          reason instanceof Error ? reason.message : 'The measurement could not run.',
        );
    } finally {
      // A card that is gone leaves the answer for the next one; a live card has shown it or let it go.
      if (cardLive.current) {
        if (owns()) setShownMeasurement(record);
        else if (measurementInFlight === record) measurementInFlight = null;
      }
      release(owner);
    }
  };
  useEffect(() => {
    if (shownMeasurement && measurementInFlight === shownMeasurement) measurementInFlight = null;
  }, [shownMeasurement]);
  // A measurement this card's predecessor started is still this card's to show, even if it
  // settled between the first render and this effect.
  useEffect(() => {
    const record = adopted;
    if (!record) return;
    const owner = { jobId: record.jobId };
    measurementOwner.current = owner;
    void follow(record, owner);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per card: the record was taken at the first render.
  }, []);

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const measureNow = async () => {
    const job = jobs.find((item) => item.id === jobId);
    if (!job || measurementOwner.current) return;
    // The same guard the main check honours: a measurement taken while the
    // machine is busy answers a different question, and answers it wrong.
    const api = desktop();
    if (!api) {
      setMeasureError('Paige cannot confirm that this computer is free for a measurement.');
      return;
    }
    // Admission is part of this exact job's action, not an unowned wait before
    // the action. Lock synchronously before the first await and retain that
    // identity through progress and the final result.
    const owner = { jobId: job.id };
    measurementOwner.current = owner;
    const owns = () =>
      cardLive.current && measurementOwner.current === owner && selectedJob.current === owner.jobId;
    setCheckingMeasurement(true);
    setMeasureError(null);
    setQueueBlockers([]);
    setQueueBlocked(false);
    let started = false;
    try {
      const availabilityNow = await api.getSpeedCheckAvailability();
      if (!owns()) return;
      if (!availabilityNow.canRun) {
        const queued = availabilityNow.blockers.some(
          (blocker) => blocker === 'captures-waiting' || blocker === 'captures-failed',
        );
        setQueueBlocked(queued);
        setMeasureError(
          queued
            ? 'Unfinished imports are preventing this measurement. They may be paused, not actively processing.'
            : availabilityNow.blockers.map((blocker) => blockerPhrasing[blocker]).join(' '),
        );
        if (queued) {
          const allJobs = await api.listJobs();
          const details = await Promise.all(
            allJobs.map(async (item) => {
              try {
                const captures = await api.listStagedCaptures(item.id);
                const pending = captures
                  .filter((capture) => capture.status === 'pending')
                  .reduce((sum, capture) => sum + capture.fileCount, 0);
                const failed = captures
                  .filter((capture) => capture.status === 'failed')
                  .reduce((sum, capture) => sum + capture.fileCount, 0);
                return captures.length
                  ? `${item.name}: ${pending} files waiting${failed ? `, ${failed} files could not finish` : ''}`
                  : null;
              } catch {
                return `${item.name}: queue status could not be read`;
              }
            }),
          );
          if (owns())
            setQueueBlockers(details.filter((detail): detail is string => detail !== null));
        }
        return;
      }
      // One measurement at a time, whichever card started it.
      if (measurementInFlight && !measurementInFlight.settled) return;
      const record = beginMeasurement(api, job);
      started = true;
      setCheckingMeasurement(false);
      setMeasuring(true);
      setMeasured(null);
      setMeasureProgress(null);
      setMeasureStartedAt(record.startedAt);
      void follow(record, owner);
    } catch (reason) {
      if (owns())
        setMeasureError(
          reason instanceof Error ? reason.message : 'The measurement could not run.',
        );
    } finally {
      if (!started) release(owner);
    }
  };

  const historyHeading = (
    <h2>
      <Activity size={18} aria-hidden="true" /> Performance over time
    </h2>
  );
  const historyArt = (
    <svg className="speed-history-art" viewBox="0 0 76 80" aria-hidden="true">
      <rect
        className="history-paper-back"
        x="18"
        y="12"
        width="43"
        height="58"
        rx="5"
        transform="rotate(8 39 41)"
      />
      <rect className="history-paper" x="11" y="8" width="43" height="58" rx="5" />
      <path className="history-ink" d="M21 22h18M21 32h23M21 41h16" />
      <circle className="history-clock" cx="53" cy="57" r="15" />
      <path className="history-hand" d="M53 48v9l6 4" />
    </svg>
  );
  if (jobs.length === 0) {
    return (
      <section className="card speed-job" aria-label="Performance over time">
        <header className="speed-job-head">{historyHeading}</header>
        {jobsLoading ? (
          <p className="speed-read-pending" role="status">
            <LoaderCircle className="spin" size={15} /> Reading job history…
          </p>
        ) : jobsError ? (
          <ComputerReadFailure
            code="job-speed-load-failed"
            title="Jobs could not be read"
            detail={jobsError}
            onRetry={() => void loadJobs()}
          />
        ) : (
          <div className="speed-history-empty">
            {historyArt}
            <div>
              <strong>No jobs run yet</strong>
            </div>
          </div>
        )}
      </section>
    );
  }
  const points = (speedHistory?.jobId === jobId ? speedHistory.batches : []).slice(-20);
  const maximumRecordedSpeed = Math.max(1, ...points.map((point) => point.pagesPerMinute));
  const axisStep = 10 ** Math.floor(Math.log10(maximumRecordedSpeed));
  const best = Math.ceil(maximumRecordedSpeed / axisStep) * axisStep;
  const first = points[0] ?? null;
  const latest = points[points.length - 1] ?? null;
  // One dot per recorded batch, in the API's chronological order. Connecting
  // actual readings does not imply comparable batches or predict improvement.
  const plot = points.map((point, index) => ({
    point,
    x: points.length > 1 ? (index / (points.length - 1)) * 100 : 50,
    y: 100 - (point.pagesPerMinute / best) * 100,
  }));

  return (
    <section className="card speed-job" aria-label="Performance over time">
      <header className="speed-job-head">
        <div className="speed-history-heading">{historyHeading}</div>
        <div className="speed-job-tools">
          <label className="speed-history-job">
            <span>Job</span>
            <select
              value={jobId}
              onChange={(event) => setJobId(event.target.value)}
              aria-label="Job to show"
              disabled={measuring || checkingMeasurement}
            >
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.name}
                </option>
              ))}
            </select>
          </label>
          <TooltipButton
            className="ghost-button"
            onClick={() => void measureNow()}
            disabled={
              measuring ||
              checkingMeasurement ||
              historyLoading ||
              Boolean(historyError) ||
              points.length === 0
            }
            tooltip="Re-reads a few pages; nothing changes"
          >
            {checkingMeasurement ? (
              <>
                <LoaderCircle className="spin" size={14} /> Checking availability…
              </>
            ) : measuring ? (
              <>
                <LoaderCircle className="spin" size={14} /> Measuring…
              </>
            ) : (
              <>
                <Gauge size={14} /> Measure now
              </>
            )}
          </TooltipButton>
        </div>
      </header>
      {checkingMeasurement && (
        <p className="speed-read-pending" role="status">
          Checking whether this computer is free to measure this job…
        </p>
      )}
      {measuring && (
        <div className="speed-measure-live" role="status" aria-live="polite">
          <div className="speed-bar">
            <i style={{ width: `${Math.round(measureShown.current.fraction * 100)}%` }} />
          </div>
          <p>
            <strong>
              {!measureProgress
                ? 'Waking the reader - warm-up is not counted in your result'
                : measureStage === 'recognizing'
                  ? 'Reading page text'
                  : measureStage === 'organizing'
                    ? 'Grouping pages into documents'
                    : measureStage === 'understanding'
                      ? 'Understanding documents'
                      : measureStage === 'finalizing'
                        ? 'Finishing up'
                        : 'Preparing'}
            </strong>
            {measureProgress &&
              measureCurrent &&
              measureProgress.total > 0 &&
              ` - ${measureProgress.completed} of ${measureProgress.total}`}
            {measureProgress &&
              measureProgress.pageCount > 0 &&
              ` · ${measureProgress.pageCount.toLocaleString()} pages read`}
            {measureStartedAt !== null &&
              ` · ${Math.max(0, Math.round((Date.now() - measureStartedAt) / 1000))}s elapsed`}
          </p>
          <small>Timing a sample of this job's recent pages. Saved documents stay unchanged.</small>
        </div>
      )}
      {measureError && (
        <p className="speed-job-verdict" role="alert" data-telemetry="error:job-speed-failed">
          <TriangleAlert size={14} /> {measureError}
        </p>
      )}
      {queueBlocked && (
        <div className="speed-job-blockers">
          <ul>
            {queueBlockers.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>
          <p>
            For this computer's performance score, run the sample test with imports temporarily
            paused. They resume automatically afterward.
          </p>
          <button
            type="button"
            data-telemetry="speed-test-job-card"
            className="primary-button"
            onClick={onPerformanceTest}
          >
            Pause imports and test this computer
          </button>
          <button type="button" className="quiet-button" onClick={onOpenJobs}>
            Open scan jobs
          </button>
        </div>
      )}
      {historyError && (
        <ComputerReadFailure
          code="job-speed-load-failed"
          title="This job's speed history could not be read"
          detail={historyError}
          onRetry={() => setHistoryRetry((value) => value + 1)}
        />
      )}
      {measured?.jobId === jobId && (
        <div className="speed-history-measured" role="status">
          <span>
            {measuredEarlierAt === null
              ? 'Sample measured now'
              : `Sample measured at ${measuredTime(measuredEarlierAt)}`}
          </span>
          <strong>{formatPagesPerMinute(measured.pagesPerMinute)}</strong>
          <p>
            {measured.pages.toLocaleString()} pages across {measured.documents.toLocaleString()}{' '}
            {measured.documents === 1 ? 'document' : 'documents'}
            {` in ${Math.round(measured.elapsedSeconds)}s.`} Type chosen from learning:{' '}
            {measured.documents > 0 ? Math.round(measured.recognisedShare * 100) : 0}%.
          </p>
        </div>
      )}
      {historyLoading && speedHistory === null ? (
        <p className="speed-read-pending" role="status">
          <LoaderCircle className="spin" size={14} /> Reading this job's speed history…
        </p>
      ) : historyError ? null : points.length === 0 ? (
        <div className="speed-history-empty">
          {historyArt}
          <div>
            <strong>No recorded batches yet</strong>
            <p>Process a batch in this job to see its recorded speed.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="speed-history-record">
            <div className="speed-history-latest">
              <span>Latest recorded speed</span>
              <div className="speed-history-rate">
                <strong>
                  {formatPagesPerMinute(latest!.pagesPerMinute).replace(' pages/min', '')}
                </strong>
                <span>pages/min</span>
              </div>
              <p>
                {latest!.pages.toLocaleString()} pages{' '}
                <span>
                  across {latest!.documents.toLocaleString()}{' '}
                  {latest!.documents === 1 ? 'document' : 'documents'}
                </span>
              </p>
            </div>
            <figure
              className={`speed-history-figure${points.length === 1 ? ' single-reading' : ''}`}
              aria-label="Recorded batch speeds"
            >
              <div className="speed-history-chart">
                <div className="speed-history-scale" aria-hidden="true">
                  <span>{best.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                  <span>{(best / 2).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                  <span>0</span>
                </div>
                <div className="speed-history-plot">
                  <svg
                    className="speed-history-trace"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label={`${points.length} recorded ${points.length === 1 ? 'batch' : 'batches'} in pages per minute${points.length > 1 ? ', oldest to newest' : ''}. Exact values in Run details.`}
                  >
                    {[0, 50, 100].map((y) => (
                      <line
                        key={y}
                        className="speed-history-grid"
                        x1="0"
                        x2="100"
                        y1={y}
                        y2={y}
                        vectorEffect="non-scaling-stroke"
                      />
                    ))}
                    {points.length > 1 && (
                      <polyline
                        className="speed-history-line"
                        points={plot.map(({ x, y }) => `${x},${y}`).join(' ')}
                        vectorEffect="non-scaling-stroke"
                      />
                    )}
                  </svg>
                  {plot.map(({ point, x, y }) => (
                    <svg
                      key={point.batchId}
                      className="speed-history-dot"
                      viewBox="0 0 12 12"
                      style={{ left: `${x}%`, top: `${y}%` }}
                      aria-hidden="true"
                    >
                      <TooltipTitle>
                        {dateLabel(point.completedAt)} ·{' '}
                        {formatPagesPerMinute(point.pagesPerMinute)}
                      </TooltipTitle>
                      <circle cx="6" cy="6" r={point === latest ? 4 : 3} />
                    </svg>
                  ))}
                </div>
              </div>
              <div className="speed-history-dates">
                <span>{dateLabel(first!.completedAt)}</span>
                {points.length > 1 && <span>{dateLabel(latest!.completedAt)}</span>}
              </div>
              <figcaption>
                {points.length === 1 ? '1 completed batch' : 'Completed batches, oldest to newest'}
              </figcaption>
            </figure>
          </div>
          <details className="speed-history-details" key={jobId}>
            <summary>
              <ChevronRight size={14} aria-hidden="true" />
              <span>Run details</span>
              <small>
                {points.length === 20
                  ? 'Latest 20 batches'
                  : `${points.length} ${points.length === 1 ? 'batch' : 'batches'}`}
              </small>
            </summary>
            <table className="speed-job-batches">
              <caption className="sr-only">Recorded batches, newest first</caption>
              <thead>
                <tr>
                  <th scope="col">Completed</th>
                  <th scope="col">Speed</th>
                  <th scope="col">Type chosen from learning</th>
                </tr>
              </thead>
              <tbody>
                {points
                  .slice()
                  .reverse()
                  .map((point) => (
                    <tr key={point.batchId}>
                      <th scope="row">
                        {dateLabel(point.completedAt)}
                        <small>
                          {point.pages.toLocaleString()} pages / {point.documents.toLocaleString()}{' '}
                          documents
                        </small>
                      </th>
                      <td>{formatPagesPerMinute(point.pagesPerMinute)}</td>
                      <td>
                        {point.documents > 0
                          ? `${point.recognisedWithoutReader.toLocaleString()} of ${point.documents.toLocaleString()} (${Math.round(point.recognisedShare * 100)}%)`
                          : '-'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <p className="speed-fineprint">
              Type chosen from learning describes how the document type was identified. Field and
              line-item reading may still be needed.
            </p>
          </details>
          <p className="speed-fineprint">
            Recorded batch speeds. Contents and document lengths vary.
          </p>
        </>
      )}
    </section>
  );
}

/** "4 minutes", "35 seconds" — a duration a person reads without decoding. */
const spokenDuration = (milliseconds: number) => {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  // Rounding to the nearest five seconds reads better than a jittering exact
  // count, but it must not round a run that has just begun UP to five seconds it
  // has not spent — the clock's whole job is to be believable.
  if (seconds < 5) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  if (seconds < 90) return `${Math.round(seconds / 5) * 5} seconds`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? `${hours} hour${hours === 1 ? '' : 's'}`
    : `${hours} hour${hours === 1 ? '' : 's'} ${rest} minute${rest === 1 ? '' : 's'}`;
};

function RunningCard({
  state,
  onStop,
}: {
  state: Extract<SpeedCheckState, { kind: 'running' }>;
  onStop: () => Promise<unknown>;
}) {
  const { progress } = state;
  // Stop takes a moment to wind the test down: the button says so at once, for this run. A stop
  // that could not be asked for gives the button back.
  const [stoppingRun, setStoppingRun] = useState<string | null>(null);
  const stopping = stoppingRun === progress.runId;
  const stop = () => {
    const runId = progress.runId;
    setStoppingRun(runId);
    onStop().catch(() => setStoppingRun((current) => (current === runId ? null : current)));
  };
  // A clock the operator can watch move. The complaint this answers is not that the
  // check is slow, it is that a bar creeping a percent a minute is indistinguishable
  // from one that has hung, and nothing on the screen said otherwise.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const began = Date.parse(progress.startedAt);
  const elapsed = Number.isFinite(began) ? Math.max(0, now - began) : 0;

  return (
    <section className="card speed-running" aria-live="polite">
      <div className="speed-running-head">
        <LoaderCircle className="spin" size={20} />
        <div>
          <strong>
            {progress.phase === 'preparing'
              ? 'Preparing the reader'
              : `${speedCheckDepths[progress.depth].title} in progress`}
          </strong>
          <small>
            {progress.datasetTitle && progress.datasetCount > 0
              ? `${progress.datasetTitle} - sample set ${progress.datasetIndex + 1} of ${progress.datasetCount}`
              : 'Getting ready'}
          </small>
        </div>
        <button className="ghost-button" onClick={stop} disabled={stopping}>
          {stopping ? (
            'Stopping…'
          ) : (
            <>
              <Square size={15} /> Stop
            </>
          )}
        </button>
      </div>
      <div
        className="speed-bar"
        role="progressbar"
        aria-label="Computer speed test progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={
          progress.phase === 'preparing' ? undefined : Math.round(progress.fraction * 100)
        }
      >
        <i style={{ width: `${Math.round(progress.fraction * 100)}%` }} />
      </div>
      <p className="speed-clock">
        <span>
          {spokenDuration(progress.phaseElapsedMs ?? elapsed)}{' '}
          {progress.phase === 'preparing' ? 'preparing' : 'so far'}
        </span>
        <span aria-hidden="true"> · </span>
        <span>
          {/* Stage counts are not time weights: CPU and GPU stage costs differ. */}
          {progress.phase === 'preparing'
            ? 'Preparation is not included in your score (up to two minutes)'
            : "Measuring this computer's speed"}
        </span>
      </p>
      <p className="speed-step">{progress.step}</p>
      <p className="speed-fineprint">
        You can stop this at any time and nothing is lost - it starts again from the beginning next
        time. Paige also stops it automatically the moment you start scanning or importing, so it
        will never get in the way of real work.
      </p>
    </section>
  );
}
